import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getUserProfile, updateUserProfile, UserProfile } from '../../api/users'
import { useAuth } from '../../AuthContext'

const roleLabels: Record<string, string> = {
  user: 'Пользователь',
  system_admin: 'Системный администратор',
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('ru-RU')
}

function extractError(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message
  const apiErr = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
  return apiErr ?? fallback
}

const UserProfilePage: React.FC = () => {
  const { userId: userIdParam } = useParams()
  const userId = Number(userIdParam)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user: currentUser, refreshUser } = useAuth()

  const [name, setName] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [adminNotes, setAdminNotes] = useState('')
  const [profileError, setProfileError] = useState<string | null>(null)
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', 'profile', userId],
    queryFn: () => getUserProfile(userId),
    enabled: userId > 0,
  })

  useEffect(() => {
    if (data?.user) {
      setName(data.user.name)
      setAdminNotes(data.user.admin_notes ?? '')
    }
  }, [data?.user?.id, data?.user?.name, data?.user?.admin_notes])

  const saveProfileMutation = useMutation({
    mutationFn: async () => {
      if (newPassword && newPassword !== confirmPassword) {
        throw new Error('Новый пароль и подтверждение не совпадают')
      }
      const payload: {
        name?: string
        current_password?: string
        new_password?: string
      } = { name: name.trim() }
      if (newPassword) {
        payload.current_password = currentPassword
        payload.new_password = newPassword
      }
      return updateUserProfile(userId, payload)
    },
    onSuccess: async () => {
      setProfileError(null)
      setProfileSuccess('Профиль сохранён')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      void queryClient.invalidateQueries({ queryKey: ['users', 'profile', userId] })
      if (currentUser?.id === userId) {
        await refreshUser()
      }
    },
    onError: (err: unknown) => {
      setProfileSuccess(null)
      setProfileError(extractError(err, 'Не удалось сохранить профиль'))
    },
  })

  const saveNotesMutation = useMutation({
    mutationFn: () =>
      updateUserProfile(userId, {
        admin_notes: adminNotes.trim() === '' ? null : adminNotes.trim(),
      }),
    onSuccess: () => {
      setProfileError(null)
      setProfileSuccess('Заметки администратора сохранены')
      void queryClient.invalidateQueries({ queryKey: ['users', 'profile', userId] })
    },
    onError: (err: unknown) => {
      setProfileSuccess(null)
      setProfileError(extractError(err, 'Не удалось сохранить заметки'))
    },
  })

  if (!userId || Number.isNaN(userId)) {
    return (
      <ProfileLayout>
        <p className="text-sm text-red-700">Некорректный ID пользователя</p>
      </ProfileLayout>
    )
  }

  if (isLoading) {
    return (
      <ProfileLayout>
        <p className="text-sm text-gray-600">Загрузка профиля...</p>
      </ProfileLayout>
    )
  }

  if (error || !data) {
    return (
      <ProfileLayout>
        <p className="text-sm text-red-700">Пользователь не найден.</p>
        <button
          type="button"
          className="mt-3 text-sm text-blue-600 hover:underline"
          onClick={() => navigate(-1)}
        >
          Назад
        </button>
      </ProfileLayout>
    )
  }

  const { user, can_edit_profile, can_edit_admin_notes, is_self } = data

  return (
    <ProfileLayout>
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-sm text-blue-600 hover:underline mb-4"
      >
        ← Назад
      </button>

      <ProfileHeader
        user={user}
        isSelf={is_self}
        showAccessLink={currentUser?.role === 'system_admin' && !is_self}
      />

      {(profileError || profileSuccess) && (
        <div
          className={`mb-4 rounded px-3 py-2 text-sm ${
            profileError ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
          }`}
        >
          {profileError ?? profileSuccess}
        </div>
      )}

      <section className="mb-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Информация</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <InfoRow label="Email" value={user.email} />
          <InfoRow label="Роль" value={roleLabels[user.role] ?? user.role} />
          <InfoRow label="Email подтверждён" value={user.email_verified ? 'Да' : 'Нет'} />
          <InfoRow label="Аккаунт активен" value={user.is_active === 1 ? 'Да' : 'Нет'} />
          <InfoRow label="Регистрация" value={formatDate(user.created_at)} />
          <InfoRow label="Обновлён" value={formatDate(user.updated_at)} />
        </dl>
      </section>

      {can_edit_profile && (
        <section className="border-t pt-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Редактирование профиля</h2>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              setProfileSuccess(null)
              saveProfileMutation.mutate()
            }}
          >
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Имя</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                required
                maxLength={255}
              />
            </div>

            <PasswordSection
              currentPassword={currentPassword}
              newPassword={newPassword}
              confirmPassword={confirmPassword}
              onCurrentChange={setCurrentPassword}
              onNewChange={setNewPassword}
              onConfirmChange={setConfirmPassword}
            />

            <button
              type="submit"
              disabled={saveProfileMutation.isPending}
              className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              {saveProfileMutation.isPending ? 'Сохранение…' : 'Сохранить профиль'}
            </button>
          </form>
        </section>
      )}

      {can_edit_admin_notes && (
        <section className="border-t pt-5 mt-5 -mx-5 -mb-5 px-5 pb-5 rounded-b-lg bg-amber-50/60">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">Заметки администратора</h2>
          <p className="text-xs text-gray-500 mb-3">Видны только системным администраторам.</p>
          <textarea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            rows={5}
            className="w-full border rounded px-3 py-2 text-sm bg-white"
            placeholder="Кто этот пользователь, зачем доступ…"
          />
          <button
            type="button"
            disabled={saveNotesMutation.isPending}
            onClick={() => {
              setProfileSuccess(null)
              saveNotesMutation.mutate()
            }}
            className="mt-3 px-4 py-2 rounded bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
          >
            {saveNotesMutation.isPending ? 'Сохранение…' : 'Сохранить заметки'}
          </button>
        </section>
      )}

      {is_self && (
        <p className="mt-4 text-xs text-gray-500 border-t pt-4">
          Ссылку на профиль можно отправить коллегам — они увидят имя и email (без заметок
          администратора).
        </p>
      )}
    </ProfileLayout>
  )
}

function ProfileLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-blue-600">
        <div className="container mx-auto px-4 py-2 text-white text-lg font-bold">
          Профиль пользователя
        </div>
      </header>
      <main className="container mx-auto px-4 py-6 max-w-2xl">
        <div className="bg-white rounded-lg shadow-md p-5">{children}</div>
      </main>
    </div>
  )
}

function ProfileHeader({
  user,
  isSelf,
  showAccessLink,
}: {
  user: UserProfile
  isSelf: boolean
  showAccessLink: boolean
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{user.name}</h1>
        <p className="text-sm text-gray-600 mt-1">{user.email}</p>
        <span className="inline-block mt-2 text-xs font-medium bg-gray-100 text-gray-700 px-2 py-1 rounded">
          {roleLabels[user.role] ?? user.role}
          {isSelf ? ' · это вы' : ''}
        </span>
      </div>
      {showAccessLink && (
        <Link
          to={`/admin/users/${user.id}/access`}
          className="text-xs font-semibold text-blue-700 bg-blue-50 px-3 py-2 rounded hover:bg-blue-100"
        >
          Права доступа →
        </Link>
      )}
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900 font-medium">{value}</dd>
    </div>
  )
}

function PasswordSection({
  currentPassword,
  newPassword,
  confirmPassword,
  onCurrentChange,
  onNewChange,
  onConfirmChange,
}: {
  currentPassword: string
  newPassword: string
  confirmPassword: string
  onCurrentChange: (v: string) => void
  onNewChange: (v: string) => void
  onConfirmChange: (v: string) => void
}) {
  return (
    <div className="border-t pt-4">
      <p className="text-xs font-medium text-gray-600 mb-2">Смена пароля (необязательно)</p>
      <div className="space-y-3">
        <PasswordInput
          label="Текущий пароль"
          value={currentPassword}
          onChange={onCurrentChange}
          autoComplete="current-password"
        />
        <PasswordInput
          label="Новый пароль"
          value={newPassword}
          onChange={onNewChange}
          autoComplete="new-password"
          minLength={6}
        />
        <PasswordInput
          label="Подтверждение"
          value={confirmPassword}
          onChange={onConfirmChange}
          autoComplete="new-password"
        />
      </div>
    </div>
  )
}

function PasswordInput({
  label,
  value,
  onChange,
  autoComplete,
  minLength,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  autoComplete: string
  minLength?: number
}) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border rounded px-3 py-2 text-sm"
        autoComplete={autoComplete}
        minLength={minLength}
      />
    </div>
  )
}

export default UserProfilePage
